import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, matchPath } from 'react-router-dom';
import { Button, Select } from '@gelirgider/ui';
import { LogOut, Menu, Monitor, Moon, Sun, Languages } from 'lucide-react';
import { useAuthStore, type AuthState } from '../../lib/stores/auth-store';
import { api } from '../../lib/api/client';
import { useUIStore, type Theme } from '../../lib/stores/ui-store';
import { ADMIN_SUPPORTED_LOCALES } from '../../i18n';

interface TopbarProps {
  onOpenSidebar?: () => void;
}

const ROUTE_TITLES: Array<{ path: string; key: string }> = [
  { path: '/', key: 'nav.dashboard' },
  { path: '/users', key: 'nav.users' },
  { path: '/users/:id', key: 'nav.users' },
  { path: '/exchange-rates', key: 'nav.exchangeRates' },
  { path: '/tags', key: 'nav.tags' },
  { path: '/activity-logs', key: 'nav.activityLogs' },
  { path: '/site-settings', key: 'nav.siteSettings' },
  { path: '/benefit-cards', key: 'nav.benefitCards' },
  { path: '/billing-metrics', key: 'nav.billingMetrics' },
  { path: '/template-reports', key: 'nav.templateReports' },
];

function useCurrentTitleKey(): string {
  const { pathname } = useLocation();
  for (const route of ROUTE_TITLES) {
    if (matchPath({ path: route.path, end: route.path === '/' }, pathname)) return route.key;
  }
  return 'topbar.title';
}

const LANG_LABELS: Record<string, string> = {
  tr: 'Türkçe',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  nl: 'Nederlands',
};

export function Topbar({ onOpenSidebar }: TopbarProps = {}): React.ReactElement {
  const { t, i18n } = useTranslation('admin');
  const navigate = useNavigate();
  const user = useAuthStore((s: AuthState) => s.user);
  const token = useAuthStore((s: AuthState) => s.accessToken);
  const clear = useAuthStore((s: AuthState) => s.clear);
  const theme = useUIStore((s) => s.theme);
  const setTheme = useUIStore((s) => s.setTheme);
  const titleKey = useCurrentTitleKey();

  async function handleLogout(): Promise<void> {
    try {
      await api('/auth/logout', { method: 'POST', body: {}, token });
    } catch {
      // ignore
    }
    clear();
    void navigate('/login', { replace: true });
  }

  function cycleTheme(): void {
    const next: Theme = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light';
    setTheme(next);
  }

  const ThemeIcon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const themeLabel =
    theme === 'light' ? t('topbar.themeLight') : theme === 'dark' ? t('topbar.themeDark') : t('topbar.themeSystem');

  return (
    <header className="flex h-16 items-center justify-between gap-2 border-b bg-card px-4 sm:px-6">
      <div className="flex min-w-0 items-center gap-2">
        {onOpenSidebar ? (
          <button
            type="button"
            aria-label={t('topbar.openMenu')}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground md:hidden"
            onClick={onOpenSidebar}
          >
            <Menu className="h-5 w-5" />
          </button>
        ) : null}
        <div className="min-w-0">
          <h1 className="truncate text-base font-semibold sm:text-lg">{t(titleKey)}</h1>
          {user ? (
            <p className="hidden truncate text-xs text-muted-foreground sm:block">
              {t('topbar.loggedInAs', { name: user.name })}
            </p>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:gap-2">
        <div className="hidden items-center gap-1 sm:flex">
          <Languages className="h-4 w-4 text-muted-foreground" />
          <Select
            value={i18n.language}
            onChange={(e) => void i18n.changeLanguage(e.target.value)}
            aria-label={t('topbar.language')}
            className="h-9 w-[120px] py-1 text-xs"
          >
            {ADMIN_SUPPORTED_LOCALES.map((lng) => (
              <option key={lng} value={lng}>
                {LANG_LABELS[lng]}
              </option>
            ))}
          </Select>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={cycleTheme}
          aria-label={t('topbar.theme')}
          title={themeLabel}
        >
          <ThemeIcon className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={handleLogout} aria-label={t('topbar.logout')}>
          <LogOut className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">{t('topbar.logout')}</span>
        </Button>
      </div>
    </header>
  );
}
